/* Dependencies */
import anime from 'animejs';

// Models
import {
  MegaMenuMobileProps,
  MenuItemLevel2,
  MenuItemLevel3,
} from './MegaMenuMobile.model';

/**
 * Mega Menu Mobile
 * @class
 */
class MegaMenuMobile {
  // Element References
  menuElement: HTMLElement;
  drawerElement: HTMLElement;
  triggerElement: HTMLElement;
  backButtonElement: HTMLElement;

  // State
  isOpen = false;
  menuItems: MenuItemLevel2[] = [];
  level2Active: MenuItemLevel2;
  level3Active: MenuItemLevel3;

  constructor(props: MegaMenuMobileProps) {
    this.menuElement = props.menuElement;
    this.drawerElement = props.drawerElement;
    this.triggerElement = props.triggerElement;
    this.backButtonElement = props.backElement;

    this.defineElements();
    this.matchBannerColor();
  }

  /**
   * Match the menu to the banner color.
   */
  matchBannerColor(): void {
    // Check if the banner match is present
    const colorMatch: HTMLElement = document.querySelector(
      '[data-menu-match-color]'
    );

    // Locate content container
    const containerElement: HTMLElement =
      this.menuElement.querySelector('[data-menu-top]');

    // If not present, return
    // If not present, return
    if (!colorMatch) {
      if (document.body.classList.contains('tw-bg-peach')) {
        this.menuElement.classList.add('tw-bg-peach');
      } else {
        this.menuElement.classList.add('tw-bg-white');
      }
      return;
    }

    // Apply color
    this.menuElement.style.backgroundColor = colorMatch.style.backgroundColor;
    containerElement.style.backgroundColor = colorMatch.style.backgroundColor;
  }

  /**
   * Define the menu elements.
   */
  defineElements(): void {
    // Locate the level 1 items
    const level1Items: HTMLElement[] = Array.from(
      this.drawerElement.querySelectorAll('[data-menu-level-2-trigger]')
    );

    // Loop over and process each menu item.
    level1Items.forEach((level1Item) => {
      const drawerElement: HTMLElement = level1Item.parentElement.querySelector(
        '[data-menu-level-2]'
      ) as HTMLElement;

      // Check and grab level 3 items.
      const level3Items: HTMLElement[] = drawerElement
        ? Array.from(
            drawerElement.querySelectorAll('[data-menu-level-3-trigger]')
          )
        : null;

      // Store the menu items.
      this.menuItems.push({
        triggerElement: level1Item,
        drawer: {
          drawerElement,
          menuItems: level3Items
            ? level3Items.map((level3Item) => {
                const drawerElement = level3Item.parentElement.querySelector(
                  '[data-menu-level-3]'
                ) as HTMLElement;
                return {
                  triggerElement: level3Item,
                  drawerElement: drawerElement,
                };
              })
            : null,
        },
      });
    });

    // Attach the required event listeners.
    this.attachEvents();
  }

  /**
   * Attach the required DOM Events.
   */
  attachEvents(): void {
    // Trigger event.
    this.triggerElement.addEventListener('click', this.toggleDrawer.bind(this));

    // Global close event.
    document.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      if (!target.closest('[data-mega-menu-mobile-drawer]') && this.isOpen) {
        this.closeDrawer();
        return;
      }
    });

    // Bind Back Button
    this.backButtonElement.addEventListener('click', () => {
      if (this.level3Active) {
        this.closeLevel3();
      } else if (this.level2Active) {
        this.closeLevel2();
      }
    });

    // Bind Level Based Events
    this.menuItems.forEach((menuItem) => {
      menuItem.triggerElement.addEventListener('click', () => {
        this.openLevel2(menuItem);
      });

      if (menuItem.drawer && menuItem.drawer.menuItems) {
        menuItem.drawer.menuItems.forEach((menuItem) => {
          menuItem.triggerElement.addEventListener('click', () => {
            this.openLevel3(menuItem);
          });
        });
      }
    });
  }

  /**
   * Toggle's the drawer open / closed.
   */
  toggleDrawer(): void {
    this.isOpen ? this.closeDrawer() : this.openDrawer();
  }

  /**
   * Locks / Unlocks the body scrolling.
   * @param status - Status to be set.
   */
  lockBodyScroll(status: boolean): void {
    document.body.style.overflow = status ? 'hidden' : null;
    document.body.setAttribute('data-mobile-menu-open', status.toString());
  }

  /**
   * Open the navigation drawer.
   */
  openDrawer(): void {
    // Ignore if already open.
    if (this.isOpen) return;

    // Lock the body scrolling.
    this.lockBodyScroll(true);

    // Animate the drawer into view.
    anime({
      begin: () => {
        // Hide the menu
        this.drawerElement.classList.remove('tw-hidden');
        this.drawerElement.classList.add('tw-flex');
      },
      targets: this.drawerElement,
      translateY: ['-100%', 0],
      duration: 200,
      easing: 'linear',
      complete: () => {
        // Update state
        this.isOpen = true;
        this.setDrawerExpanded(true);
      },
    });
  }

  /**
   * Close the navigation drawer.
   */
  closeDrawer(): void {
    // Ignore if already closed.
    if (!this.isOpen) return;

    // Animate the drawer out of view.
    anime({
      targets: this.drawerElement,
      translateY: [0, '-100%'],
      duration: 200,
      easing: 'linear',
      complete: () => {
        // Update state
        this.isOpen = false;
        this.setDrawerExpanded(false);
        this.resetMenu();

        // Unlock the body scrolling.
        this.lockBodyScroll(false);

        // Hide the menu
        this.drawerElement.classList.remove('tw-flex');
        this.drawerElement.classList.add('tw-hidden');
      },
    });
  }

  /**
   * Opens the level 2 menu.
   * @param menuItem - Menu item to be opened.
   */
  openLevel2(menuItem: MenuItemLevel2): void {
    // Ignore if already open.
    if (this.level2Active) return;

    // Update state.
    this.level2Active = menuItem;

    // Animate the menu into view.
    anime({
      begin: () => {
        // Set Visible
        menuItem.drawer.drawerElement.classList.remove('tw-hidden');
      },
      targets: menuItem.drawer.drawerElement,
      left: ['100%', '0'],
      easing: 'linear',
      duration: 200,
      complete: () => {
        this.setLevelExpanded(this.level2Active.triggerElement, true);
        this.showBackButton(true);
      },
    });
  }

  /**
   * Close the level 2 menu.
   */
  closeLevel2(): void {
    // Ignore if already closed.
    if (!this.level2Active) return;

    // Extract the active elements.
    const { drawer, triggerElement } = this.level2Active;

    // Animate the menu out of view.
    anime({
      targets: drawer.drawerElement,
      left: ['0', '100%'],
      easing: 'linear',
      duration: 200,
      complete: () => {
        // Update State
        this.setLevelExpanded(triggerElement, false);
        this.level2Active = null;
        this.showBackButton(false);

        // Set hidden
        drawer.drawerElement.classList.add('tw-hidden');
      },
    });
  }

  /**
   * Opens the level 3 menu.
   * @param menuItem - Menu item to be opened.
   */
  openLevel3(menuItem: MenuItemLevel3): void {
    // Ignore if already open.
    if (this.level3Active) return;

    // Update state.
    this.level3Active = menuItem;

    //  Set the second level list to not overflow.
    this.level2Active.drawer.drawerElement.style.overflow = 'hidden';

    // Animate the menu into view.
    anime({
      begin: () => {
        // Set Visible
        menuItem.drawerElement.classList.remove('tw-hidden');
      },
      targets: menuItem.drawerElement,
      left: ['100%', '0'],
      easing: 'linear',
      duration: 200,
      complete: () => {
        this.setLevelExpanded(menuItem.triggerElement, true);
      },
    });
  }

  /**
   * Closes the level 3 menu.
   */
  closeLevel3(): void {
    // Ignore if already closed.
    if (!this.level3Active) return;

    // Extract the active elements.
    const { drawerElement, triggerElement } = this.level3Active;

    // Release the scroll lock on level 2
    this.level2Active.drawer.drawerElement.style.overflow = 'auto';

    // Animate the menu out of view.
    anime({
      targets: drawerElement,
      left: ['0', '100%'],
      easing: 'linear',
      duration: 200,
      complete: () => {
        this.setLevelExpanded(triggerElement, false);
        // Set hidden
        drawerElement.classList.add('tw-hidden');
        this.level3Active = null;
      },
    });
  }

  /**
   * Sets if the back button should be visible.
   * @param status - The status to set.
   */
  showBackButton(status: boolean): void {
    if (status) {
      this.backButtonElement.classList.remove('tw-hidden');
      this.backButtonElement.classList.add('tw-flex');
    } else {
      this.backButtonElement.classList.add('tw-hidden');
      this.backButtonElement.classList.remove('tw-flex');
    }
  }

  /**
   * Sets the ariua lables for the drawer.
   * @param status - Status to be set.
   */
  setDrawerExpanded(status: boolean): void {
    this.triggerElement.setAttribute('aria-expanded', status.toString());
  }

  /**
   * Sets the aria lables for the level 2 & 3 trigger.
   * @param status - Status to be set.
   */
  setLevelExpanded(element: HTMLElement, status: boolean): void {
    element.setAttribute('aria-expanded', status.toString());
  }

  /**
   * Resets the menu to it's initial state.
   */
  resetMenu() {
    this.closeLevel3();
    this.closeLevel2();
  }
}

/**
 * Initiates the mobile menu.
 */
export const megaMenuMobileInit = (): void => {
  // Retrieve the required elements.
  const menuElement: HTMLElement = document.querySelector(
    '[data-mega-menu-mobile]'
  );

  const triggerElement: HTMLElement = document.querySelector(
    '[data-mega-menu-mobile-trigger]'
  );

  const drawerElement: HTMLElement = document.querySelector(
    '[data-mega-menu-mobile-drawer]'
  );

  const backElement: HTMLElement = document.querySelector(
    '[data-mega-menu-mobile-back]'
  );

  // Ignore if menu is not found.
  if (!menuElement) return;

  // Create a new mobile menu.
  new MegaMenuMobile({
    menuElement,
    drawerElement,
    triggerElement,
    backElement,
  });
};
